// libs
import * as React from 'react'
import moment from 'moment'
import { useQuery } from '@apollo/client/react'
// constants
import { ENTITIES } from 'constants/entities'
import { TASKS_TYPES } from 'constants/home'
// graphql
import { REVIEWS_TO_APPROVE_QUERY } from 'graphql/reviews/queries'
// hooks
import { useCurrentUser } from 'hooks/useCurrentUser'
// types
import { IReview } from 'types/graphql'
import { ITask } from 'containers/StrategyActions/components/TasksList/layers/API'
// utils
import get from 'lodash/get'
import { getReviewDueDate, isReviewApprover } from 'utils/reviews'

const getReviewedEntity = (review: IReview) => {
  if (review.planObjectiveId) {
    return { entityType: ENTITIES.PLAN_OBJECTIVE, entityId: review.planObjectiveId }
  }
  if (review.planActionId) {
    return { entityType: ENTITIES.PLAN_ACTION, entityId: review.planActionId }
  }
  if (review.planKPIId) {
    return { entityType: ENTITIES.PLAN_KPI, entityId: review.planKPIId }
  }
  return { entityType: ENTITIES.PLAN, entityId: review.planId }
}

// Reviews waiting for the current user's approval, as strategy-actions tasks
export const useReviewsToApproveTasks = (): ITask[] => {
  const currentUser = useCurrentUser()

  const { data: reviewsData } = useQuery(REVIEWS_TO_APPROVE_QUERY, { fetchPolicy: 'network-only' })

  // approvedAt/status are re-checked here so an approved review drops out as soon as the cache has it
  const reviews: IReview[] = React.useMemo(
    () => get(reviewsData, 'reviewsToApprove.data', []).filter((review) => isReviewApprover(review, currentUser)),
    // useCurrentUser returns a new object on every render
    [reviewsData, currentUser.id]
  )

  // memoized: consumers remount task lists on a new array, and each mount adds to the shared counters
  return React.useMemo(
    () =>
      reviews.map((review) => {
        const { entityType, entityId } = getReviewedEntity(review)

        const endPeriod = moment(review.endPeriodAt)
        const dueDate = getReviewDueDate({ ...review, endPeriod })

        return {
          id: entityId,
          // for an item's review, the plan the item belongs to (none for items outside plans)
          planId: review.planId,
          type: TASKS_TYPES.APPROVAL,
          entityType,
          description: review.planItemName || '',
          path: entityType !== ENTITIES.PLAN ? review.planName || '' : '',
          review: { ...review, startPeriod: moment(review.startPeriod), endPeriod, dueDate },
          dueDate,
          isOpen: true,
          // an approval is never overdue: the due date belongs to the reviewers
          isOverdue: false,
          // approvals are the current user's own; reviewers stay on task.review for the avatars
          assignedPersons: [currentUser],
          currentPermissions: {}
        }
      }),
    [reviews]
  )
}
